import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useMemo, useReducer } from "react";
import { useSelector, useDispatch } from "react-redux";
import { X } from "lucide-react";
import SaveIcon from "@mui/icons-material/Save";
import { Dialog, DialogTitle, DialogContent, DialogActions, Radio, RadioGroup, FormControlLabel, FormControl } from "@mui/material";
import { TextInput } from "../../../common_components/FormInputs";
import { fetchClientConnections, fetchNetworkAccounts, syncNetworkAccounts, connectDisconnectAccount, connectShopify, connectKlaviyo, deleteShopifyFilter, deleteKlaviyoFilter, deleteConnection, selectConnectionsByClient, selectOldConnectionsByClient, selectNetworkAccounts, selectNetworkAccountsLoading, selectNetworkAccountsSyncing, selectShopifyConnecting, selectKlaviyoConnecting, selectFilterDeleting, selectConnectionActionLoading, selectConnectionsLoading, selectNetworkAccountsPagination, selectKlaviyoDeleting, selectConnectionDeleting, deleteConnectionById, setNetworkAccountApiEnabled, 
// fetchShopifyInstallUrl,
sendShopifyInstallEmail, } from "../../../redux/clientDataSlice";
import ConfirmActionModal from "../../../common_components/ConfirmActionModal";
import AppButton from "../../../common_components/AppButton";
import "../../../styles/AdsDrawer.css";
import CommonDrawerLayout from "../../../common_components/CommonDrawerLayout";
import ImagePreviewModal from "./components/ImagePreviewModal";
import ShopifySection from "./components/ShopifySection";
import KlaviyoSection from "./components/KlaviyoSection";
import AccountsTable from "./components/AccountsTable";
/* ================= CUSTOM HOOKS ================= */
// ✅ Custom hook for debounced search
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}
const modalReducer = (state, action) => {
    switch (action.type) {
        case "OPEN_CONFIRM":
            return {
                ...state,
                confirm: { open: true, account: action.payload.account, action: action.payload.action },
            };
        case "CLOSE_CONFIRM":
            return { ...state, confirm: { open: false, account: null, action: "" } };
        case "OPEN_CLEANUP":
            return { ...state, cleanup: { open: true, account: action.payload } };
        case "CLOSE_CLEANUP":
            return { ...state, cleanup: { open: false, account: null } };
        case "OPEN_DELETE_FILTER":
            return {
                ...state,
                deleteFilter: { open: true, filter: action.payload.filter, index: action.payload.index },
            };
        case "CLOSE_DELETE_FILTER":
            return { ...state, deleteFilter: { open: false, filter: null, index: null } };
        case "OPEN_DELETE_ACCOUNT":
            return { ...state, deleteAccount: { open: true, account: action.payload } };
        case "CLOSE_DELETE_ACCOUNT":
            return { ...state, deleteAccount: { open: false, account: null } };
        case "OPEN_DELETE_KLAVIYO_KEY":
            return { ...state, deleteKlaviyoKey: { open: true } };
        case "CLOSE_DELETE_KLAVIYO_KEY":
            return { ...state, deleteKlaviyoKey: { open: false } };
        case "OPEN_DELETE_SHOPIFY_CONNECTION":
            return { ...state, deleteShopifyConnection: { open: true } };
        case "CLOSE_DELETE_SHOPIFY_CONNECTION":
            return { ...state, deleteShopifyConnection: { open: false } };
        case "OPEN_UPDATE_CONFIRM":
            return { ...state, updateConfirm: { open: true } };
        case "CLOSE_UPDATE_CONFIRM":
            return { ...state, updateConfirm: { open: false } };
        default:
            return state;
    }
};
/* ================= MAIN COMPONENT ================= */
const AdsNetworkAccount = ({ open, onClose, network, clientId, clientName, refetchClientList }) => {
    const dispatch = useDispatch();
    /* -------- REDUX SELECTORS -------- */
    const client = useSelector((state) => state.clientData.clientsById[clientId]);
    const networkConnections = useSelector((state) => selectConnectionsByClient(clientId, network?.id)(state));
    const oldNetworkConnections = useSelector((state) => selectOldConnectionsByClient(clientId, network?.id)(state));
    const networkAccounts = useSelector(selectNetworkAccounts);
    const isAccountsLoading = useSelector(selectNetworkAccountsLoading);
    const isConnectionsLoading = useSelector(selectConnectionsLoading);
    const isSyncing = useSelector(selectNetworkAccountsSyncing);
    const isShopifyConnecting = useSelector(selectShopifyConnecting);
    const isKlaviyoConnecting = useSelector(selectKlaviyoConnecting);
    const isFilterDeleting = useSelector(selectFilterDeleting);
    const isConnectionActionLoading = useSelector(selectConnectionActionLoading);
    const networkAccountsPagination = useSelector(selectNetworkAccountsPagination);
    /* -------- LOCAL STATE -------- */
    const [searchText, setSearchText] = useState("");
    const debouncedSearch = useDebounce(searchText, 500);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [filtersModified, setFiltersModified] = useState(false);
    const [hasExistingConnection, setHasExistingConnection] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [activeImage, setActiveImage] = useState(null);
    // ✅ Use Set instead of array for better performance
    const [connectedAccountIds, setConnectedAccountIds] = useState(new Set());
    const [orphanedConnections, setOrphanedConnections] = useState([]);
    // Shopify
    const [shopifyStoreUrl, setShopifyStoreUrl] = useState("");
    const [shopifyTimezone, setShopifyTimezone] = useState("");
    const [shopifyFirstOrderDate, setShopifyFirstOrderDate] = useState("");
    const [shopifyAccessToken, setShopifyAccessToken] = useState("");
    const [shopifyOauthClientId, setShopifyOauthClientId] = useState("");
    const [shopifyOauthClientSecret, setShopifyOauthClientSecret] = useState("");
    const [isEditingShopifyCreds, setIsEditingShopifyCreds] = useState(false);
    const [installationLink, setInstallationLink] = useState("");
    const [shopifyConnectionId, setShopifyConnectionId] = useState("");
    const [sendLinkModalOpen, setSendLinkModalOpen] = useState(false);
    const [emailList, setEmailList] = useState([]);
    const [emailInput, setEmailInput] = useState("");
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [initialShopifyCreds, setInitialShopifyCreds] = useState({
        storeUrl: "",
        oauthClientId: "",
        oauthClientSecret: "",
        installationLink: "",
    });
    const [shopifyFilters, setShopifyFilters] = useState([
        { field: "sales_channel", operator: "NOT CONTAINS", value: "pos" },
        { field: "sales_channel", operator: "NOT CONTAINS", value: "Point of Sale" },
    ]);
    const [shopifyRevenueSettings, setShopifyRevenueSettings] = useState({
        account_summary: "G-D+S+T",
        performance_tracker: "G-D+S+T",
        client_report: "G-D+S+T",
        divergence_report: "G-D+S+T",
        commission_tracker: "netsales",
    });
    const [shopifyReturnPolicy, setShopifyReturnPolicy] = useState("60");
    const [shopifyApiEnabled, setShopifyApiEnabled] = useState(false);
    const [sortConfig, setSortConfig] = useState(null);
    const handleSortChange = useCallback((columnId, direction) => {
        setSortConfig(columnId && direction ? { columnId, direction } : null);
    }, []);
    // Klaviyo
    const [klaviyoApiKey, setKlaviyoApiKey] = useState("");
    const [klaviyoApiEnabled, setKlaviyoApiEnabled] = useState(true);
    const [klaviyoFilters, setKlaviyoFilters] = useState([
        { field: "", operator: "is", value: "" },
    ]);
    // ✅ NEW: Add selector
    const isKlaviyoDeleting = useSelector(selectKlaviyoDeleting);
    const [initialKlaviyoApiKey, setInitialKlaviyoApiKey] = useState("");
    // ✅ NEW: Initial modal state update
    const [modals, dispatchModal] = useReducer(modalReducer, {
        confirm: { open: false, account: null, action: "" },
        cleanup: { open: false, account: null },
        deleteFilter: { open: false, filter: null, index: null },
        deleteAccount: { open: false, account: null },
        deleteKlaviyoKey: { open: false },
        deleteShopifyConnection: { open: false },
        updateConfirm: { open: false },
    });
    const [refreshDataOption, setRefreshDataOption] = useState("refresh");
    const [klaviyoMetricId, setKlaviyoMetricId] = useState("");
    // ✅ NEW: Delete Klaviyo Key Handler
    const handleDeleteKlaviyoKey = useCallback(() => {
        dispatchModal({ type: "OPEN_DELETE_KLAVIYO_KEY" });
    }, []);
    // ✅ NEW: Confirm Delete Klaviyo Key
    // ✅ Updated: Confirm Delete Klaviyo Key
    const handleDeleteKlaviyoKeyConfirm = useCallback(async () => {
        if (!network?.id)
            return;
        const activeConnection = networkConnections.find(c => c.status === "active");
        if (!activeConnection?._id)
            return;
        try {
            await dispatch(deleteConnectionById({
                connectionId: activeConnection._id,
            })).unwrap();
            // Reset state after successful deletion
            setKlaviyoApiKey("");
            setKlaviyoMetricId("");
            setKlaviyoFilters([{ field: "", operator: "is", value: "" }]);
            setHasExistingConnection(false);
            setFiltersModified(false);
            // Refetch connections
            dispatch(fetchClientConnections({ clientId, network: network.id }));
            refetchClientList?.();
        }
        catch (error) {
            console.error("Delete Klaviyo connection failed:", error);
        }
        finally {
            dispatchModal({ type: "CLOSE_DELETE_KLAVIYO_KEY" });
        }
    }, [dispatch, clientId, network?.id, networkConnections, refetchClientList]);
    const isShopify = network?.id === "shopify";
    const isKlaviyo = network?.id === "klaviyo";
    const isOtherNetwork = !isShopify && !isKlaviyo;
    const handleOpenImage = useCallback((key) => {
        setActiveImage(key);
    }, []);
    const handleCloseImage = useCallback(() => {
        setActiveImage(null);
    }, []);
    const isSaving = isShopifyConnecting || isKlaviyoConnecting || isConnectionActionLoading;
    const hasShopifyToken = Boolean(shopifyAccessToken.trim());
    const activeShopifyConnection = isShopify
        ? networkConnections.find(c => c.status === "active" || c.status === "inactive")
        : null;
    const currentInstallLink = installationLink ||
        (activeShopifyConnection?.installation_link ?? "");
    const currentConnectionId = shopifyConnectionId ||
        String(activeShopifyConnection?._id ?? "");
    const isSaveDisabled = useMemo(() => {
        if (isSaving)
            return true;
        if (isShopify) {
            // Token exists + editing credentials
            if (hasShopifyToken && isEditingShopifyCreds) {
                return !shopifyOauthClientId.trim() || !shopifyOauthClientSecret.trim() || !currentInstallLink.trim();
            }
            // Token exists + not editing (only filter/revenue changes count)
            if (hasShopifyToken && !isEditingShopifyCreds) {
                return !filtersModified;
            }
            // No token: need store URL + oauth credentials + install URL
            return !shopifyStoreUrl.trim() || !shopifyOauthClientId.trim() || !shopifyOauthClientSecret.trim() || !currentInstallLink.trim();
        }
        if (isKlaviyo) {
            if (hasExistingConnection)
                return !filtersModified;
            return !klaviyoApiKey.trim();
        }
        return true;
    }, [isSaving, isShopify, isKlaviyo, hasShopifyToken, isEditingShopifyCreds, shopifyStoreUrl, shopifyOauthClientId, shopifyOauthClientSecret, currentInstallLink, klaviyoApiKey, filtersModified, hasExistingConnection]);
    /* -------- EFFECTS -------- */
    const handleDeleteClick = useCallback((account) => {
        dispatchModal({ type: "OPEN_DELETE_ACCOUNT", payload: account });
    }, []);
    // Toggle API Enabled for a connected account (persists immediately — the table has no Save button).
    const handleApiEnabledToggle = useCallback(async (account, value) => {
        if (!account.connection_id || !network?.id)
            return;
        try {
            await dispatch(setNetworkAccountApiEnabled({
                clientId,
                network: network.id,
                connectionId: account.connection_id,
                api_enabled: value,
                accountName: account.account_name,
            })).unwrap();
            dispatch(fetchNetworkAccounts({
                network: network.id,
                clientId,
                searchText: debouncedSearch,
                page: currentPage,
                limit: pageSize,
                sortBy: sortConfig?.columnId,
                sortOrder: sortConfig?.direction,
            }));
        }
        catch (error) {
            console.error("Toggle api_enabled failed:", error);
        }
    }, [dispatch, clientId, network?.id, debouncedSearch, currentPage, pageSize, sortConfig]);
    const isConnectionDeleting = useSelector(selectConnectionDeleting);
    const handleDeleteAccountConfirm = useCallback(async () => {
        if (!modals.deleteAccount.account || !modals.deleteAccount.account.connection_id)
            return;
        try {
            // ✅ NEW API
            await dispatch(deleteConnectionById({
                connectionId: modals.deleteAccount.account.connection_id,
            })).unwrap();
            dispatch(fetchNetworkAccounts({
                network: network.id,
                clientId: clientId,
                searchText: debouncedSearch,
                page: currentPage,
                limit: pageSize,
                sortBy: sortConfig?.columnId,
                sortOrder: sortConfig?.direction
            }));
            refetchClientList?.();
        }
        catch (error) {
            console.error("Delete failed:", error);
        }
        finally {
            dispatchModal({ type: "CLOSE_DELETE_ACCOUNT" });
        }
    }, [dispatch, clientId, network?.id, modals.deleteAccount.account, debouncedSearch, currentPage, pageSize, refetchClientList]);
    // Reset state & load connections for Shopify/Klaviyo
    useEffect(() => {
        if (!open || !network?.id || !clientId)
            return;
        if (isShopify || isKlaviyo) {
            dispatch(fetchClientConnections({ clientId, network: network.id }));
        }
        // Reset state
        setCurrentPage(1);
        setSearchText("");
        setFiltersModified(false);
        return () => {
            // Cleanup on unmount
            setConnectedAccountIds(new Set());
            setOrphanedConnections([]);
        };
    }, [dispatch, open, network?.id, clientId, isShopify, isKlaviyo]);
    // Fetch network accounts with debounced search, pagination, and sorting
    useEffect(() => {
        if (!open || !network?.id || !clientId || !isOtherNetwork)
            return;
        dispatch(fetchNetworkAccounts({
            network: network.id,
            searchText: debouncedSearch,
            page: currentPage,
            clientId: clientId,
            limit: pageSize,
            sortBy: sortConfig?.columnId,
            sortOrder: sortConfig?.direction
        }));
    }, [dispatch, network?.id, debouncedSearch, currentPage, pageSize, open, isOtherNetwork, clientId, sortConfig]);
    // ✅ CONSOLIDATED: Initialize Shopify/Klaviyo from connections
    useEffect(() => {
        if (!open || !network?.id)
            return;
        const activeConnection = isShopify
            ? networkConnections.find(c => c.status === "active" || c.status === "inactive")
            : networkConnections.find(c => c.status === "active");
        if (isShopify && activeConnection) {
            setShopifyStoreUrl(activeConnection.value || "");
            setShopifyTimezone(activeConnection.timezone || "");
            setShopifyFirstOrderDate(activeConnection.first_order_date || "");
            setShopifyAccessToken(activeConnection.token || "");
            setShopifyOauthClientId(activeConnection.oauth_client_id || "");
            setShopifyOauthClientSecret(activeConnection.oauth_client_secret || "");
            setIsEditingShopifyCreds(false);
            setShopifyReturnPolicy(activeConnection.return_policy?.toString() || "");
            setShopifyApiEnabled(activeConnection.api_enabled === true);
            setHasExistingConnection(true);
            setShopifyConnectionId(activeConnection._id);
            setInitialShopifyCreds({
                storeUrl: activeConnection.value || "",
                oauthClientId: activeConnection.oauth_client_id || "",
                oauthClientSecret: activeConnection.oauth_client_secret || "",
                installationLink: activeConnection.installation_link || "",
            });
            if (activeConnection.installation_link) {
                setInstallationLink(activeConnection.installation_link);
            }
            const filters = activeConnection.filter && Array.isArray(activeConnection.filter) && activeConnection.filter.length > 0
                ? activeConnection.filter.map((f) => ({
                    field: f.field || "",
                    operator: f.operator || "CONTAINS",
                    value: f.value || ""
                }))
                : [{ field: "", operator: "CONTAINS", value: "" }];
            setShopifyFilters(filters);
            // Load Shopify revenue settings from connection or client
            if (activeConnection.shopify_revenue_settings) {
                setShopifyRevenueSettings({
                    account_summary: activeConnection.shopify_revenue_settings.account_summary || "G-D+S+T",
                    performance_tracker: activeConnection.shopify_revenue_settings.performance_tracker || "G-D+S+T",
                    client_report: activeConnection.shopify_revenue_settings.client_report || "G-D+S+T",
                    divergence_report: activeConnection.shopify_revenue_settings.divergence_report || "G-D+S+T",
                    commission_tracker: activeConnection.shopify_revenue_settings.commission_tracker || "netsales",
                });
            }
            else if (client?.clientDetails?.setting?.shopify_revenue_settings) {
                setShopifyRevenueSettings(prev => ({
                    ...prev,
                    ...client.clientDetails.setting.shopify_revenue_settings,
                }));
            }
        }
        else if (isShopify && !activeConnection) {
            setHasExistingConnection(false);
            setShopifyApiEnabled(false);
            setShopifyStoreUrl("");
            setShopifyTimezone("");
            setShopifyFirstOrderDate("");
            setShopifyAccessToken("");
            setShopifyOauthClientId("");
            setShopifyOauthClientSecret("");
            setInstallationLink("");
            setShopifyConnectionId("");
            setIsEditingShopifyCreds(false);
            setShopifyReturnPolicy("60");
            setShopifyFilters([
                { field: "sales_channel", operator: "NOT CONTAINS", value: "pos" },
                { field: "sales_channel", operator: "NOT CONTAINS", value: "Point of Sale" },
            ]);
            setShopifyRevenueSettings({
                account_summary: "G-D+S+T",
                performance_tracker: "G-D+S+T",
                client_report: "G-D+S+T",
                divergence_report: "G-D+S+T",
                commission_tracker: "netsales",
            });
        }
        if (isKlaviyo && activeConnection) {
            setKlaviyoApiKey(activeConnection.value || "");
            setInitialKlaviyoApiKey(activeConnection.value || "");
            setKlaviyoMetricId(activeConnection.token || "");
            setKlaviyoApiEnabled(activeConnection.api_enabled !== false);
            setHasExistingConnection(true);
            const filters = activeConnection.filter && Array.isArray(activeConnection.filter) && activeConnection.filter.length > 0
                ? activeConnection.filter.map((f) => ({
                    field: f.field || "",
                    operator: f.operator || "is",
                    value: f.value || ""
                }))
                : [{ field: "", operator: "is", value: "" }];
            setKlaviyoFilters(filters);
        }
        else if (isKlaviyo && !activeConnection) {
            setHasExistingConnection(false);
            setKlaviyoApiEnabled(true);
            setKlaviyoApiKey("");
            setInitialKlaviyoApiKey("");
            setKlaviyoMetricId("");
            setKlaviyoFilters([{ field: "", operator: "is", value: "" }]);
        }
    }, [networkConnections, open, network?.id, isShopify, isKlaviyo]);
    // ✅ CONSOLIDATED: Process accounts & connections for other networks
    useEffect(() => {
        if (!open || !network?.id || !isOtherNetwork)
            return;
        const activeConnections = networkConnections.filter(c => c.status === "active");
        const availableAccountIds = new Set(networkAccounts.map(acc => acc.account_id));
        const connected = new Set();
        const orphaned = [];
        activeConnections.forEach((conn) => {
            const accountId = conn.value || conn.account_id;
            if (availableAccountIds.has(accountId)) {
                connected.add(accountId);
            }
            else {
                orphaned.push({
                    connectionId: conn._id || "",
                    accountId: accountId,
                    accountName: conn.account_name || `Account ${accountId}`,
                    connectionData: conn,
                });
            }
        });
        setConnectedAccountIds(connected);
        setOrphanedConnections(orphaned);
    }, [networkConnections, networkAccounts, open, network?.id, isOtherNetwork]);
    /* -------- HANDLERS -------- */
    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
    }, []);
    const handleShopifyRevenueChange = useCallback((field, value) => {
        setShopifyRevenueSettings(prev => ({ ...prev, [field]: value }));
        setFiltersModified(true);
    }, []);
    const handleShopifyStoreUrlChange = useCallback((value) => {
        setShopifyStoreUrl(value);
        setFiltersModified(true);
    }, []);
    const handleShopifyAccessTokenChange = useCallback((value) => {
        setShopifyAccessToken(value);
        setFiltersModified(true);
    }, []);
    const handleShopifyReturnPolicyChange = useCallback((value) => {
        setShopifyReturnPolicy(value);
        setFiltersModified(true);
    }, []);
    const handleInstallationLinkChange = useCallback((value) => {
        setInstallationLink(value);
        setFiltersModified(true);
    }, []);
    const handleToggleEditShopifyCreds = useCallback(() => {
        setIsEditingShopifyCreds((prev) => {
            const nextState = !prev;
            if (!nextState) {
                // Cancel clicked, reset fields
                setShopifyOauthClientId(initialShopifyCreds.oauthClientId || "");
                setShopifyOauthClientSecret(initialShopifyCreds.oauthClientSecret || "");
                setInstallationLink(initialShopifyCreds.installationLink || "");
            }
            return nextState;
        });
    }, [initialShopifyCreds]);
    const handleKlaviyoApiKeyChange = useCallback((value) => {
        setKlaviyoApiKey(value);
        setFiltersModified(true);
    }, []);
    const handleSyncAccounts = useCallback(async () => {
        if (!network?.id || isSyncing || isRefreshing)
            return;
        setIsRefreshing(true);
        try {
            await dispatch(syncNetworkAccounts({ network: network.id })).unwrap();
            await dispatch(fetchNetworkAccounts({
                network: network.id,
                searchText: debouncedSearch,
                page: currentPage,
                clientId: clientId,
                limit: pageSize,
                sortBy: sortConfig?.columnId,
                sortOrder: sortConfig?.direction
            })).unwrap();
        }
        catch (error) {
            console.error("Sync failed:", error);
        }
        finally {
            setIsRefreshing(false);
        }
    }, [dispatch, network?.id, debouncedSearch, currentPage, pageSize, isSyncing, isRefreshing, clientId]);
    const handleCheckboxClick = useCallback((account) => {
        const statusLower = String(account.status || "").toLowerCase().trim();
        const isDelete = statusLower === "delete";
        const isActive = statusLower === "active";
        const isConnected = Boolean(account.connected);
        const action = (isDelete || (isConnected && isActive)) ? "disconnect" : "connect";
        dispatchModal({ type: "OPEN_CONFIRM", payload: { account, action } });
    }, []);
    const handleConfirmModalAction = useCallback(async () => {
        if (!modals.confirm.account || !modals.confirm.action || !network?.id)
            return;
        try {
            await dispatch(connectDisconnectAccount({
                clientId,
                network: network.id,
                accountId: modals.confirm.account.account_id,
                action: modals.confirm.action,
                accountName: modals.confirm.account.account_name,
            })).unwrap();
            dispatch(fetchNetworkAccounts({
                network: network.id,
                searchText: debouncedSearch,
                page: currentPage,
                clientId: clientId,
                limit: pageSize,
                sortBy: sortConfig?.columnId,
                sortOrder: sortConfig?.direction
            }));
        }
        catch (error) {
            console.error("Connect/Disconnect failed:", error);
        }
        finally {
            dispatchModal({ type: "CLOSE_CONFIRM" });
        }
    }, [dispatch, clientId, network?.id, modals.confirm, debouncedSearch, currentPage, pageSize]);
    // Handler to open modal
    const handleDeleteShopifyConnection = useCallback(() => {
        dispatchModal({ type: "OPEN_DELETE_SHOPIFY_CONNECTION" });
    }, []);
    const [oldEntryToDelete, setOldEntryToDelete] = useState(null);
    const [deleteOldEntryModalOpen, setDeleteOldEntryModalOpen] = useState(false);
    const handleDeleteOldEntryClick = useCallback((entry) => {
        setOldEntryToDelete(entry);
        setDeleteOldEntryModalOpen(true);
    }, []);
    const confirmDeleteOldEntry = useCallback(async () => {
        if (!oldEntryToDelete?._id)
            return;
        try {
            await dispatch(deleteConnectionById({
                connectionId: oldEntryToDelete._id,
            })).unwrap();
            setDeleteOldEntryModalOpen(false);
            setOldEntryToDelete(null);
            // Re-fetch connections to update lists
            dispatch(fetchClientConnections({ clientId, network: network?.id }));
        }
        catch (error) {
            console.error(error);
        }
    }, [dispatch, oldEntryToDelete, clientId, network?.id]);
    // Confirm handler
    const handleDeleteShopifyConnectionConfirm = useCallback(async () => {
        if (!network?.id)
            return;
        const activeConnection = networkConnections.find(c => c.status === "active" || c.status === "inactive");
        if (!activeConnection?._id)
            return;
        try {
            await dispatch(deleteConnectionById({
                connectionId: activeConnection._id,
            })).unwrap();
            // Reset state
            setShopifyStoreUrl("");
            setShopifyTimezone("");
            setShopifyAccessToken("");
            setShopifyOauthClientId("");
            setShopifyOauthClientSecret("");
            setInstallationLink("");
            setShopifyFilters([{ field: "", operator: "CONTAINS", value: "" }]);
            setHasExistingConnection(false);
            setFiltersModified(false);
            dispatch(fetchClientConnections({ clientId, network: network.id }));
            refetchClientList?.();
        }
        catch (error) {
            console.error("Delete Shopify connection failed:", error);
        }
        finally {
            dispatchModal({ type: "CLOSE_DELETE_SHOPIFY_CONNECTION" });
        }
    }, [dispatch, clientId, network?.id, networkConnections, refetchClientList]);
    const handleCleanupClick = useCallback((orphaned) => {
        dispatchModal({ type: "OPEN_CLEANUP", payload: orphaned });
    }, []);
    const handleCleanupConfirm = useCallback(async () => {
        if (!modals.cleanup.account)
            return;
        try {
            await dispatch(deleteConnection({
                clientId,
                connectionId: modals.cleanup.account.connectionId,
            })).unwrap();
            setOrphanedConnections(prev => prev.filter(conn => conn.connectionId !== modals.cleanup.account.connectionId));
            setConnectedAccountIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(modals.cleanup.account.accountId);
                return newSet;
            });
        }
        catch (error) {
            console.error("Cleanup failed:", error);
        }
        finally {
            dispatchModal({ type: "CLOSE_CLEANUP" });
        }
    }, [dispatch, clientId, modals.cleanup.account]);
    const handleDeleteFilterClick = useCallback((index, filter) => {
        dispatchModal({ type: "OPEN_DELETE_FILTER", payload: { filter, index } });
    }, []);
    const handleDeleteFilterConfirm = useCallback(async () => {
        if (modals.deleteFilter.index === null || !network?.id)
            return;
        try {
            if (isShopify && shopifyStoreUrl) {
                await dispatch(deleteShopifyFilter({
                    clientId,
                    storeUrl: shopifyStoreUrl,
                    filterIndex: modals.deleteFilter.index,
                })).unwrap();
                setShopifyFilters(prev => {
                    const newFilters = [...prev];
                    newFilters.splice(modals.deleteFilter.index, 1);
                    return newFilters.length > 0 ? newFilters : [{ field: "", operator: "CONTAINS", value: "" }];
                });
                dispatch(fetchClientConnections({ clientId, network: network.id }));
            }
            if (isKlaviyo && klaviyoApiKey) {
                await dispatch(deleteKlaviyoFilter({
                    clientId,
                    accountKey: klaviyoApiKey,
                    filterIndex: modals.deleteFilter.index,
                })).unwrap();
                setKlaviyoFilters(prev => {
                    const newFilters = [...prev];
                    newFilters.splice(modals.deleteFilter.index, 1);
                    return newFilters.length > 0 ? newFilters : [{ field: "", operator: "is", value: "" }];
                });
                dispatch(fetchClientConnections({ clientId, network: network.id }));
            }
        }
        catch (error) {
            console.error("Delete filter failed:", error);
        }
        finally {
            dispatchModal({ type: "CLOSE_DELETE_FILTER" });
        }
    }, [dispatch, clientId, network?.id, modals.deleteFilter.index, isShopify, isKlaviyo, shopifyStoreUrl, klaviyoApiKey]);
    const executeSave = useCallback(async (refreshData = false) => {
        if (!network?.id)
            return;
        try {
            if (isShopify && shopifyStoreUrl.trim()) {
                const credsChanged = shopifyStoreUrl !== initialShopifyCreds.storeUrl ||
                    shopifyOauthClientId !== initialShopifyCreds.oauthClientId ||
                    shopifyOauthClientSecret !== initialShopifyCreds.oauthClientSecret;
                let installationLink = "";
                // if (
                //     (!hasExistingConnection || credsChanged) &&
                //     shopifyOauthClientId.trim() &&
                //     shopifyOauthClientSecret.trim()
                // ) {
                //     try {
                //         console.log("calling install-url");
                //         const installRes: any = await dispatch(
                //             fetchShopifyInstallUrl({
                //                 oauth_client_id: shopifyOauthClientId,
                //                 oauth_client_secret: shopifyOauthClientSecret,
                //                 shop: shopifyStoreUrl,
                //                 client_id: clientId,
                //                 refreshData: refreshData,
                //             })
                //         ).unwrap();
                //         const installationLink = installRes?.installation_link || "";
                //         setInstallationLink(installationLink);
                //         setShopifyConnectionId(installRes?.connection_id || "");
                //     } catch (err) {
                //         console.error("install-url failed", err);
                //         return; // 🛑 STOP EXECUTION IF INSTALL URL FAILS
                //     }
                //     setIsEditingShopifyCreds(false);
                // }
                // Step 2: connect/update Shopify ALWAYS run
                const result = await dispatch(connectShopify({
                    clientId,
                    connectionId: shopifyConnectionId,
                    storeUrl: shopifyStoreUrl,
                    accessToken: shopifyAccessToken,
                    oauthClientId: shopifyOauthClientId || undefined,
                    oauthClientSecret: shopifyOauthClientSecret || undefined,
                    filters: shopifyFilters,
                    shopifyRevenueSettings,
                    returnPolicy: shopifyReturnPolicy
                        ? parseInt(shopifyReturnPolicy)
                        : undefined,
                    refreshData,
                    isUpdateOnly: !credsChanged,
                    installation_link: installationLink || currentInstallLink,
                    api_enabled: shopifyApiEnabled,
                })).unwrap();
                setHasExistingConnection(true);
                if (result.filters?.length > 0) {
                    setShopifyFilters(result.filters);
                }
                // update snapshot after successful save
                setInitialShopifyCreds({
                    storeUrl: shopifyStoreUrl,
                    oauthClientId: shopifyOauthClientId,
                    oauthClientSecret: shopifyOauthClientSecret,
                    installationLink: installationLink || currentInstallLink,
                });
                dispatch(fetchClientConnections({
                    clientId,
                    network: network.id
                }));
                refetchClientList?.();
                setFiltersModified(false);
            }
            if (isKlaviyo) {
                const result = await dispatch(connectKlaviyo({
                    clientId,
                    accountKey: klaviyoApiKey,
                    filters: klaviyoFilters,
                    refreshData,
                    api_enabled: klaviyoApiEnabled,
                })).unwrap();
                setHasExistingConnection(true);
                setFiltersModified(false);
                setInitialKlaviyoApiKey(klaviyoApiKey);
                if (result.filters?.length > 0) {
                    setKlaviyoFilters(result.filters);
                }
                dispatch(fetchClientConnections({
                    clientId,
                    network: network.id
                }));
                refetchClientList?.();
            }
            dispatchModal({ type: "CLOSE_UPDATE_CONFIRM" });
        }
        catch (error) {
            console.error("Save error:", error);
        }
    }, [
        dispatch,
        clientId,
        network,
        isShopify,
        isKlaviyo,
        hasExistingConnection,
        initialShopifyCreds,
        shopifyStoreUrl,
        shopifyAccessToken,
        shopifyOauthClientId,
        shopifyOauthClientSecret,
        shopifyFilters,
        shopifyRevenueSettings,
        shopifyReturnPolicy,
        shopifyApiEnabled,
        klaviyoApiKey,
        klaviyoFilters,
        klaviyoApiEnabled,
        refetchClientList,
        currentInstallLink
    ]);
    const credsChanged = shopifyStoreUrl !== initialShopifyCreds.storeUrl ||
        shopifyOauthClientId !== initialShopifyCreds.oauthClientId ||
        shopifyOauthClientSecret !== initialShopifyCreds.oauthClientSecret ||
        currentInstallLink !== initialShopifyCreds.installationLink;
    const klaviyoCredsChanged = klaviyoApiKey !== initialKlaviyoApiKey;
    const handleSave = useCallback(async () => {
        if (isShopify &&
            hasExistingConnection &&
            isEditingShopifyCreds &&
            credsChanged) {
            dispatchModal({ type: "OPEN_UPDATE_CONFIRM" });
            return;
        }
        if (isKlaviyo &&
            hasExistingConnection &&
            klaviyoCredsChanged) {
            dispatchModal({ type: "OPEN_UPDATE_CONFIRM" });
            return;
        }
        await executeSave(false);
    }, [
        isShopify,
        isKlaviyo,
        hasExistingConnection,
        isEditingShopifyCreds,
        credsChanged,
        klaviyoCredsChanged,
        executeSave
    ]);
    /* -------- INSTALL LINK HANDLERS -------- */
    const openSendLinkModal = useCallback(() => {
        const contacts = (client?.clientDetails?.client_contacts || [])
            .map((c) => c.email)
            .filter(Boolean);
        setEmailList(contacts.length > 0 ? contacts : []);
        setEmailInput("");
        setSendLinkModalOpen(true);
    }, [client]);
    const handleAddEmail = useCallback(() => {
        const val = emailInput.trim().toLowerCase();
        if (!val || emailList.includes(val))
            return;
        setEmailList(prev => [...prev, val]);
        setEmailInput("");
    }, [emailInput, emailList]);
    const handleRemoveEmail = useCallback((email) => {
        setEmailList(prev => prev.filter(e => e !== email));
    }, []);
    const handleSendInstallEmail = useCallback(async () => {
        const connId = currentConnectionId;
        if (!connId || emailList.length === 0)
            return;
        setIsSendingEmail(true);
        try {
            await dispatch(sendShopifyInstallEmail({
                connection_id: connId,
                emails: emailList,
            })).unwrap();
            setSendLinkModalOpen(false);
        }
        catch (err) {
            console.error("Send install email failed", err);
        }
        finally {
            setIsSendingEmail(false);
        }
    }, [dispatch, currentConnectionId, emailList]);
    /* -------- EARLY RETURN -------- */
    if (!open)
        return null;
    const headerActions = (isShopify || isKlaviyo) ? (_jsx(AppButton, { variant: "contained", onClick: handleSave, startIcon: _jsx(SaveIcon, {}), disabled: isSaveDisabled, color: "primary", children: isSaving ? "Saving..." : "Save" })) : null;
    /* -------- RENDER -------- */
    return (_jsxs(_Fragment, { children: [_jsxs(CommonDrawerLayout, { open: open, onClose: onClose, title: `${network?.name} | ${clientName || "Pro Standard"}`, headerActions: headerActions, isLoading: (isShopify || isKlaviyo) ? isConnectionsLoading : false, width: "md", children: [isShopify && (_jsx(ShopifySection, { hasExistingConnection: hasExistingConnection, hasToken: hasShopifyToken, timezone: shopifyTimezone, firstOrderDate: shopifyFirstOrderDate, filtersModified: filtersModified, storeUrl: shopifyStoreUrl, accessToken: shopifyAccessToken, onAccessTokenChange: handleShopifyAccessTokenChange, oauthClientId: shopifyOauthClientId, oauthClientSecret: shopifyOauthClientSecret, filters: shopifyFilters, revenueSettings: shopifyRevenueSettings, onStoreUrlChange: handleShopifyStoreUrlChange, onOauthClientIdChange: setShopifyOauthClientId, onOauthClientSecretChange: setShopifyOauthClientSecret, onFiltersChange: setShopifyFilters, onRevenueSettingsChange: handleShopifyRevenueChange, onDeleteFilter: handleDeleteFilterClick, onFilterModified: () => setFiltersModified(true), onDeleteConnection: handleDeleteShopifyConnection, isDeleting: isConnectionDeleting, returnPolicy: shopifyReturnPolicy, onReturnPolicyChange: handleShopifyReturnPolicyChange, isEditingCreds: isEditingShopifyCreds, onToggleEditCreds: handleToggleEditShopifyCreds, installationLink: currentInstallLink, onInstall: () => window.open(currentInstallLink, "_blank"), onSendLink: openSendLinkModal, connectionStatus: activeShopifyConnection?.status, onOpenImage: handleOpenImage, oldEntries: oldNetworkConnections, onDeleteOldEntry: handleDeleteOldEntryClick, onInstallationLinkChange: handleInstallationLinkChange, apiEnabled: shopifyApiEnabled, onApiEnabledChange: (val) => { setShopifyApiEnabled(val); setFiltersModified(true); } })), isKlaviyo && (_jsx(KlaviyoSection, { hasExistingConnection: hasExistingConnection, filtersModified: filtersModified, apiKey: klaviyoApiKey, filters: klaviyoFilters, onApiKeyChange: handleKlaviyoApiKeyChange, onFiltersChange: setKlaviyoFilters, onDeleteFilter: handleDeleteFilterClick, onFilterModified: () => setFiltersModified(true), onOpenImage: handleOpenImage, onDeleteApiKey: handleDeleteKlaviyoKey, metricId: klaviyoMetricId, oldEntries: oldNetworkConnections, onDeleteOldEntry: handleDeleteOldEntryClick, apiEnabled: klaviyoApiEnabled, onApiEnabledChange: (val) => { setKlaviyoApiEnabled(val); setFiltersModified(true); } })), isOtherNetwork && network && (_jsx(AccountsTable, { network: network, accounts: networkAccounts, orphanedConnections: orphanedConnections, connectedAccountIds: connectedAccountIds, onCheckboxClick: handleCheckboxClick, onCleanupClick: handleCleanupClick, searchText: searchText, onSearchChange: setSearchText, onSync: handleSyncAccounts, isLoading: isAccountsLoading || isSyncing || isRefreshing, isSyncing: isSyncing, 
                        // onDeleteClick={handleDeleteAccount}
                        pagination: networkAccountsPagination, onPageChange: handlePageChange, onDeleteClick: handleDeleteClick, onApiEnabledToggle: handleApiEnabledToggle, sortConfig: sortConfig, onSortChange: handleSortChange }))] }), _jsx(ConfirmActionModal, { open: modals.confirm.open, title: modals.confirm.action === "connect" ? "Connect Account" : "Disconnect Account", description: `Are you sure you want to ${modals.confirm.action} "${modals.confirm.account?.account_name}"?`, confirmText: "Yes", cancelText: "Cancel", isDeleteIcon: modals.confirm.action !== "connect", isConfirm: modals.confirm.action === "connect", loading: isConnectionActionLoading, onClose: () => dispatchModal({ type: "CLOSE_CONFIRM" }), onConfirm: handleConfirmModalAction }), _jsx(ConfirmActionModal, { open: modals.cleanup.open, title: "Remove Connection", description: `This account (${modals.cleanup.account?.accountId} - ${modals.cleanup.account?.accountName}) no longer exists in ${network?.name}. Do you want to remove this connection from the client?`, confirmText: "Remove", cancelText: "Cancel", isDeleteIcon: true, isConfirm: false, loading: isConnectionActionLoading, onClose: () => dispatchModal({ type: "CLOSE_CLEANUP" }), onConfirm: handleCleanupConfirm }), _jsx(ConfirmActionModal, { open: modals.deleteFilter.open, title: "Delete Filter", description: "Are you sure you want to delete this filter?", confirmText: "Delete", cancelText: "Cancel", isDeleteIcon: true, isConfirm: false, loading: isFilterDeleting, onClose: () => dispatchModal({ type: "CLOSE_DELETE_FILTER" }), onConfirm: handleDeleteFilterConfirm }), _jsx(ConfirmActionModal, { open: modals.deleteAccount.open, title: "Delete Account Connection", description: `Are you sure you want to delete the connection for "${modals.deleteAccount.account?.account_name}" (${modals.deleteAccount.account?.account_id})?  This action leads to remove all its records & cannot be undone.`, confirmText: "Delete", cancelText: "Cancel", isDeleteIcon: true, isConfirm: false, loading: isConnectionDeleting, onClose: () => dispatchModal({ type: "CLOSE_DELETE_ACCOUNT" }), onConfirm: handleDeleteAccountConfirm }), _jsx(ConfirmActionModal, { open: modals.deleteKlaviyoKey.open, title: "Delete Klaviyo Connection", description: "Are you sure you want to delete this Klaviyo connection? This action will permanently remove the connection and delete all associated records.", confirmText: "Delete", cancelText: "Cancel", isDeleteIcon: true, isConfirm: false, loading: isConnectionDeleting, onClose: () => dispatchModal({ type: "CLOSE_DELETE_KLAVIYO_KEY" }), onConfirm: handleDeleteKlaviyoKeyConfirm }), _jsx(ConfirmActionModal, { open: modals.deleteShopifyConnection.open, title: "Delete Shopify Connection", description: "Are you sure you want to delete this Shopify connection? This action will permanently remove the connection and delete all associated records.", confirmText: "Delete", cancelText: "Cancel", isDeleteIcon: true, isConfirm: false, loading: isConnectionDeleting, onClose: () => dispatchModal({ type: "CLOSE_DELETE_SHOPIFY_CONNECTION" }), onConfirm: handleDeleteShopifyConnectionConfirm }), _jsx(ConfirmActionModal, { open: deleteOldEntryModalOpen, title: "Delete Old Connection", description: "Are you sure you want to remove this inactive connection? This action cannot be undone.", confirmText: "Delete", cancelText: "Cancel", isDeleteIcon: true, isConfirm: false, loading: false, onClose: () => setDeleteOldEntryModalOpen(false), onConfirm: confirmDeleteOldEntry }), _jsxs(Dialog, { open: modals.updateConfirm.open, onClose: () => dispatchModal({ type: "CLOSE_UPDATE_CONFIRM" }), maxWidth: "sm", fullWidth: true, sx: { zIndex: 2000 }, children: [_jsx(DialogTitle, { style: { fontWeight: 600, fontSize: "1.1rem" }, children: "Update Credentials" }), _jsxs(DialogContent, { dividers: true, children: [_jsx("p", { style: { margin: "0px", marginBottom: "8px", color: "#475467" }, children: "How would you like to handle the existing historical data for this connection?" }), _jsx(FormControl, { component: "fieldset", children: _jsxs(RadioGroup, { value: refreshDataOption, onChange: (e) => setRefreshDataOption(e.target.value), children: [_jsx(FormControlLabel, { value: "refresh", control: _jsx(Radio, { sx: { color: '#FF008E', '&.Mui-checked': { color: '#FF008E' } } }), label: _jsxs("div", { style: { marginLeft: '4px' }, children: [_jsx("div", { style: { fontWeight: 500 }, children: "Re-sync Existing Records" }), _jsxs("div", { style: { fontSize: '0.85rem', color: '#667085' }, children: ["Delete all ", network?.name || "historical", " data for this connection, clear access token, and re-sync fresh data from scratch."] })] }), style: { marginBottom: '12px' } }), _jsx(FormControlLabel, { value: "keep", control: _jsx(Radio, { sx: { color: '#FF008E', '&.Mui-checked': { color: '#FF008E' } } }), label: _jsxs("div", { style: { marginLeft: '4px' }, children: [_jsx("div", { style: { fontWeight: 500 }, children: "Keep Existing Records" }), _jsx("div", { style: { fontSize: '0.85rem', color: '#667085' }, children: "Mark current connection as inactive and create a new active entry with updated credentials. Existing data is preserved." })] }) })] }) })] }), _jsxs(DialogActions, { style: { padding: "16px 24px" }, children: [_jsx(AppButton, { variant: "outlined", onClick: () => dispatchModal({ type: "CLOSE_UPDATE_CONFIRM" }), sx: { borderColor: '#FF008E', color: '#FF008E', '&:hover': { borderColor: '#CC006F', color: '#CC006F', backgroundColor: 'transparent' } }, children: "Cancel" }), _jsx(AppButton, { variant: "contained", onClick: () => executeSave(refreshDataOption === "refresh"), disabled: isSaving, sx: { backgroundColor: '#FF008E', '&:hover': { backgroundColor: '#CC006F' }, color: '#fff' }, children: isSaving ? "Saving..." : "Confirm & Save" })] })] }), _jsx(ImagePreviewModal, { activeImage: activeImage, onClose: handleCloseImage }), _jsxs(Dialog, { open: sendLinkModalOpen, onClose: () => setSendLinkModalOpen(false), maxWidth: "xs", fullWidth: true, sx: { zIndex: 2100 }, children: [_jsx(DialogTitle, { style: { fontWeight: 600, fontSize: "1rem", paddingBottom: 4 }, children: "Send Installation Link" }), _jsxs(DialogContent, { sx: { paddingTop: '12px !important' }, children: [_jsxs("div", { style: { display: "flex", gap: "8px", alignItems: "flex-end", marginBottom: "12px" }, children: [_jsx("div", { style: { flex: 1 }, children: _jsx(TextInput, { label: "Email Address", value: emailInput, onChange: e => setEmailInput(e.target.value), placeholder: "email@example.com", onKeyDown: (e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handleAddEmail();
                                                }
                                            } }) }), _jsx(AppButton, { variant: "contained", size: "small", onClick: handleAddEmail, sx: { mb: "2px", whiteSpace: "nowrap", backgroundColor: '#FF008E', '&:hover': { backgroundColor: '#CC006F' } }, children: "Add" })] }), emailList.length > 0 && (_jsx("div", { style: {
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "6px",
                                    padding: "8px",
                                    border: "1px solid #fce7f3",
                                    borderRadius: "8px",
                                    background: "#fff7fb",
                                }, children: emailList.map(email => (_jsxs("div", { style: {
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        background: "#ffe4f3",
                                        color: "#99005a",
                                        borderRadius: "20px",
                                        padding: "3px 8px 3px 10px",
                                        fontSize: "12px",
                                        fontWeight: 500,
                                    }, children: [email, _jsx("button", { onClick: () => handleRemoveEmail(email), style: {
                                                background: "none",
                                                border: "none",
                                                cursor: "pointer",
                                                color: "#FF008E",
                                                padding: "0",
                                                display: "flex",
                                                alignItems: "center",
                                            }, children: _jsx(X, { size: 12 }) })] }, email))) }))] }), _jsxs(DialogActions, { style: { padding: "12px 16px" }, children: [_jsx(AppButton, { variant: "outlined", size: "small", onClick: () => setSendLinkModalOpen(false), sx: { borderColor: '#FF008E', color: '#FF008E', '&:hover': { borderColor: '#CC006F', color: '#CC006F', backgroundColor: 'transparent' } }, children: "Cancel" }), _jsx(AppButton, { variant: "contained", size: "small", onClick: handleSendInstallEmail, disabled: isSendingEmail || emailList.length === 0, sx: { backgroundColor: '#FF008E', '&:hover': { backgroundColor: '#CC006F' }, color: '#fff' }, children: isSendingEmail ? "Sending..." : "Send" })] })] })] }));
};
export default AdsNetworkAccount;
